import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ticker, TickerLink } from '@benzinga/ticker-ui';
import { BaseQuoteProfile, QuoteFund } from '../../utils/quote/types';
import { QuoteMetricRow } from './ETFQuoteMetrics';
import { formatPercentage, truncate } from '@benzinga/utils';
import { DateTime } from 'luxon';

export interface ETFQuoteSidebarProps {
  profile: BaseQuoteProfile;
  etfFund: QuoteFund | null;
}

export const ETFQuoteSidebar: React.FC<ETFQuoteSidebarProps> = ({ etfFund }) => {
  const { i18n } = useTranslation('quote');
  const managers = Array.isArray(etfFund?.managers) ? etfFund.managers : [];
  const fundCategory = etfFund?.fund_category?.[0]?.category || '-';
  const fundDefinition = etfFund?.fund_category?.[0]?.definition || '-';

  const fundInceptionDate = etfFund?.inception_date || null;
  const fundInceptionJSDate = fundInceptionDate ? DateTime.fromISO(fundInceptionDate ?? '') : '';
  const formattedDate = fundInceptionJSDate
    ? fundInceptionJSDate.setLocale(i18n.language).toFormat('MMM d, yyyy')
    : '-';

  const etfDetails = [
    {
      label: 'Sector',
      value: etfFund?.sector || '-',
    },
    {
      label: 'Category',
      value: fundCategory,
    },
    {
      label: 'Investment Style',
      value: etfFund?.investment_style || '-',
    },
    {
      label: 'Fund Inception',
      value: formattedDate,
    },
    {
      label: 'Managers',
      labelClassName: 'flex items-center',
      value: managers.length > 0 ? managers.map(manager => <div key={manager}>{manager}</div>) : '-',
    },
    {
      label: 'Definition',
      labelClassName: 'flex items-center',
      value: fundDefinition || '-',
    },
  ];

  const fundHolders = etfFund?.fund_top_holders || [];

  return (
    <div className="w-full flex flex-col gap-y-6">
      <div className="flex flex-col">
        <h5 className="mb-2">ETF Details</h5>
        <table className="w-full h-full">
          <tbody className="flex flex-col h-full divide-y divide-bzblue-300 px-4 py-2 rounded border border-bzblue-400">
            {etfDetails.map(metric => (
              <QuoteMetricRow
                //className={metric.className}
                key={metric.label}
                label={metric.label}
                labelClassName={metric.labelClassName}
                value={metric.value}
                //valueClassName={metric.valueClassName}
              />
            ))}
          </tbody>
        </table>
      </div>
      {etfFund?.fund_description && (
        <div className="flex flex-col">
          <h5 className="mb-2">Investment Policy</h5>
          <div className="rounded border border-bzblue-400 p-4 text-sm leading-[1.35rem]">
            {etfFund?.fund_description}
          </div>
        </div>
      )}
      {fundHolders.length > 0 && (
        <div className="flex flex-col">
          <h5 className="mb-2">Top ETF Holdings</h5>
          <table className="flex flex-col w-full h-full px-4 py-2 rounded border border-bzblue-400">
            <thead className="mb-2">
              <tr className="flex flex-row text-gray-600 text-sm text-left w-full">
                <th className="font-semibold flex-1 w-[5rem] max-w-[5rem]">Symbol</th>
                <th className="font-semibold flex-1">Name</th>
                <th className="font-semibold flex-1 text-right">Share %</th>
              </tr>
            </thead>
            <tbody className="flex flex-col h-full divide-y divide-bzblue-300 whitespace-nowrap">
              {fundHolders.map((fundHolder, index) => (
                <QuoteMetricRow
                  key={index}
                  label={
                    <Ticker
                      symbol={fundHolder.symbol}
                      targetElement={<TickerLink symbol={fundHolder.symbol}>{fundHolder.symbol}</TickerLink>}
                    />
                  }
                  labelClassName="font-bold w-[5rem] max-w-[5rem]"
                  secondValue={truncate(fundHolder.name, 22)}
                  value={fundHolder.sharepercentage ? `${formatPercentage(fundHolder.sharepercentage)}%` : '-'}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ETFQuoteSidebar;
